import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { bdCell, labelOf, splitRefs } from './bd-defs.js';
import { toneOf } from './bd-presentation.js';
import type { BdArtifactDef, BdColumnDef, BdRow } from '../../../lib/api/basic-design.js';

export interface BdRowsTableProps {
  /** 節の定義（列の見出し・選択肢の表示名） */
  def: BdArtifactDef;
  /** 保存済みの行 */
  rows: readonly BdRow[];
  /** 出す列（隠した列を除いたもの、定義の順） */
  columns: readonly BdColumnDef[];
}

/** 表の見出しのセル。 @graph-connects none */
const TH_CLASS =
  'whitespace-nowrap border-b border-[var(--color-border)] px-3 py-2 text-left text-xs font-medium text-[var(--color-text-muted)]';

/** 表の本文のセル。 @graph-connects none */
const TD_CLASS = 'border-b border-[var(--color-border)] px-3 py-2 align-top';

/**
 * 節の行を 1 行 1 段の表で読む。行を見比べたいとき（列ごとに縦に読みたいとき）のための見せ方で、
 * カードと同じく選ぶ欄は表示名の札、参照は ID の札にする。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の節の行を、選んだ列だけの表で読む
 * @graph-connects labelOf [calls] 選択肢の表示名
 * @graph-connects splitRefs [calls] 参照の分割
 */
export function BdRowsTable({ def, rows, columns }: BdRowsTableProps) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto rounded-lg border border-[var(--color-border)]">
      <table
        className="w-full border-collapse text-sm"
        aria-label={t('bd.view.rowsAria', { title: def.title })}
      >
        <thead>
          <tr>
            <th className={TH_CLASS}>#</th>
            {columns.map((column) => (
              <th key={column.key} className={TH_CLASS}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            // 同じ中身の行が並ぶことがあるので、保存済みの並びの位置で鍵を作る
            <tr key={index}>
              <td className={`${TD_CLASS} text-xs tabular-nums text-[var(--color-text-muted)]`}>
                {index + 1}
              </td>
              {columns.map((column) => (
                <td key={column.key} className={TD_CLASS}>
                  <Cell column={column} value={bdCell(row, column.key)} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** 1 つのセル。 @graph-connects none */
function Cell({ column, value }: { column: BdColumnDef; value: string }) {
  if (value.trim() === '') return null;
  if (column.kind === 'choice') {
    return (
      <Badge size="sm" variant={toneOf(column.key, value)}>
        {labelOf(column, value)}
      </Badge>
    );
  }
  if (column.kind === 'id') {
    return <code className="text-xs font-semibold text-[var(--color-text-primary)]">{value}</code>;
  }
  if (column.kind === 'text') {
    return (
      <span className="whitespace-pre-wrap break-words text-[var(--color-text-primary)]">
        {value}
      </span>
    );
  }
  return (
    <span className="flex flex-wrap gap-1">
      {splitRefs(value).map((token, i) => (
        // 同じ ID を 2 度書いた行もそのまま出すので、位置で鍵を作る
        <code
          key={`${String(i)}:${token}`}
          className="rounded bg-[var(--color-bg-elevated)] px-1.5 py-0.5 text-xs text-[var(--color-text-secondary)]"
        >
          {token}
        </code>
      ))}
    </span>
  );
}
